import React, { useMemo, useState, useCallback } from 'react';
import { scaleThreshold } from 'd3-scale';
import classnames from 'classnames';

import ChoroplethMapWidget from '~/core/scripts/widgets/ChoroplethMapWidget';
import useCsvData from '~/core/scripts/hooks/useCsvData';
import { sortByKey } from '~/core/scripts/utils';

import { createDataLookup } from './utils';
import useHolidayData from './useHolidayData';
import Table from './Table';

const sortKey = {
  sleepings: 'beliebtheit',
  infections: 'agsRel',
};

const colors = ['#94D88A', '#FFF1BD', '#FFDD9E', '#FCC08E', '#EE9F82', '#D97C7A', '#A1286A'];
const colorScale = scaleThreshold().domain([0.01, 4, 8, 20, 33, 50]).range(colors);

const colorizer = (props) => colorScale(typeof props.value !== 'undefined' ? +props.value : props);

function getMonthStr(date) {
  const month = date.getMonth() + 1;

  return month < 10 ? `0${month}` : month;
}

function getDayStr(date) {
  const day = date.getDate();

  return day < 10 ? `0${day}` : day;
}

const today = new Date('2020-09-16T00:00:00Z');
const todayStr = `${getDayStr(today)}.${getMonthStr(today)}`;
const yesterday = new Date(today.valueOf() - 86400000 * 1);
const yesterdayStr = `${getDayStr(yesterday)}.${getMonthStr(yesterday)}`
const sevenDaysAgo = new Date(today.valueOf() - 86400000 * 7);
const sevenDaysAgoStr = `${getDayStr(sevenDaysAgo)}.${getMonthStr(sevenDaysAgo)}`

export default () => {
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const [dataMode, setDataMode] = useState('sleepings');

  const data = useCsvData('data/de.districts.csv', ',', (row) => ({
    ...row,
    population: +row.population,
    confirmed: +row.confirmed,
  }));

  const lookup = useMemo(() => createDataLookup(data), [data]);
  const holidayData = useHolidayData(lookup, dataMode);
  const tableData = useMemo(() => {
    if (holidayData) {
      const d = holidayData.features.map((feat) => ({ ...feat.properties }));
      d.sort(sortByKey(sortKey[dataMode]));

      return d;
    } else {
      return [];
    }
  }, [holidayData, dataMode]);

  const onHoverCell = useCallback((record) => setHoveredRegion(record), []);
  const renderApp = data.length > 0 && holidayData;
  const isSmallScreen = window.innerWidth <= 1000;

  const renderCoro = () => (
    <div className="choropleth-wrapper">
      <ChoroplethMapWidget
        minZoom={5}
        maxZoom={10}
        choroplethSource="data/kreise-small.topo.json"
        holidayGeo={holidayData}
        overlayDissolveKey="Bundesland"
        dataLookup={lookup}
        showBasemap={false}
        valueKey="value"
        legendMinLabel="0"
        legendMaxLabel="> 50"
        showLabels
        showMapToggleButton={true}
        isFullScreen={false}
        colorizer={colorizer}
        dataMode={dataMode}
        legendColors={colors}
        hoveredRegion={hoveredRegion}
        //labelPreset="germanStates"
        //customLabels={[]}
        //searchPresets={[{ name: 'Sylt,', locationId: 'NT_1S92gH278yVBID0q0yWKdB' }, { name: 'Binz,', locationId: 'NT_westEu-lxczuwbbQj.m5mD' }, { name: 'Kempten', locationId: 'NT_mVfWIlx1ALMLsfFZRZxjZB' }]}
        searchPresets={[{name: '', locationId: 'NT_1S92gH278yVBID0q0yWKdB'}]}
        searchPresetsTitle="Zum Beispiel: Sylt, Binz, Kempten"
      />
    </div>
  );

  return (
    <div className="coroapp">
      {renderApp && !isSmallScreen && renderCoro()}

      <div className="table-wrapper">
        <div className="headline">
          <div className="topline">
            <span className="kicker">SOMMERFERIEN 2020</span>
            <time className="date">STAND: {todayStr}</time>
          </div>
          <h1 className="title">Deutschlands Urlaubsgebiete im Corona-Vergleich</h1>
          <h2 className="introtext">
            Endlich Sommerferien! Doch wegen Corona wird dieser Urlaub kein normaler. Verfolgen Sie, wo die
            Neuinfektionen seit Ferienbeginn steigen und wo Urlaub frei vom Virus möglich ist - täglich aktuell.
          </h2>
        </div>

        <div id="geocoder-portal"></div>

        <div className="datamode">
          <div className="datamode__title">Neuinfektionen in den deutschen Reisegebieten</div>
          <p>gemeldete Fälle der letzten 7 Tage ({sevenDaysAgoStr} - {yesterdayStr}) pro 100.000 Einwohner der zugehörigen Kreise (eingefärbt)
          </p>
        </div>

        {renderApp && isSmallScreen && renderCoro()}

        <div className="btn-group horizontal">
          <button
            className={classnames('btn', { active: dataMode === 'sleepings' })}
            onClick={() => setDataMode('sleepings')}
          >
            nach Beliebtheit
          </button>
          <button
            className={classnames('btn', { active: dataMode !== 'sleepings' })}
            onClick={() => setDataMode('infections')}
          >
            nach Neuinfektionen
          </button>
        </div>
        {renderApp && (
          <Table
            data={tableData}
            colorScale={colorScale}
            setDataMode={setDataMode}
            dataMode={dataMode}
            onHoverCell={onHoverCell}
          />
        )}
        <div className="datamode">
          <p><strong>Beliebtheit</strong> nach Übernachtungen im Juli/August 2019 (*&nbsp;keine Angaben)<br></br>| ↗&nbsp;von 0 Neuinfektionen | ↘&nbsp;auf 0 gefallen</p>
        </div>
      </div>
    </div>
  );
};
